import React, { useState } from 'react';
import { BookOpen, SearchIcon, X, Trash2, FolderOpen } from 'lucide-react';
import { Button, Input, Spinner } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import { Glossary } from '@/services/apis/glossaries';
import CategorySelector from '../../base/selectors/CategorySelector';
import GlossaryCard from './GlossaryCard';
import ConfirmationModal from '../../base/ConfirmationModal';
import CategoriesModal from '../CategoriesModal';

interface GlossariesTabProps {
  glossaries: Glossary[];
  isLoading: boolean;
  isFetchingNextPage: boolean;
  error: string | null;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  selectedCategoryId?: string;
  onCategoryChange: (value: string | undefined) => void;
  hasActiveFilters: boolean;
  onClearFilters: () => void;
  onEdit: (glossary: Glossary) => void;
  onDelete: (glossary: Glossary) => void;
  deleteModalOpen: boolean;
  selectedGlossary: Glossary | null;
  onDeleteConfirm: () => void;
  onDeleteModalClose: () => void;
  isDeleting: boolean;
  loadMoreRef: React.RefObject<HTMLElement> | ((node: HTMLElement | null) => void);
  hasNextPage: boolean;
  onCreate: () => void;
  selectedGlossaryIds?: string[];
  onSelectionChange?: (ids: string[]) => void;
  onBulkDelete?: () => void;
  bulkDeleteModalOpen?: boolean;
  onBulkDeleteConfirm?: () => void;
  onBulkDeleteModalClose?: () => void;
  isBulkDeleting?: boolean;
}

const GlossariesTab: React.FC<GlossariesTabProps> = ({
  glossaries,
  isLoading,
  isFetchingNextPage,
  error,
  searchQuery,
  onSearchChange,
  selectedCategoryId,
  onCategoryChange,
  hasActiveFilters,
  onClearFilters,
  onEdit,
  onDelete,
  deleteModalOpen,
  selectedGlossary: _selectedGlossary,
  onDeleteConfirm,
  onDeleteModalClose,
  isDeleting,
  loadMoreRef,
  hasNextPage,
  onCreate,
  selectedGlossaryIds = [],
  onSelectionChange,
  onBulkDelete,
  bulkDeleteModalOpen = false,
  onBulkDeleteConfirm,
  onBulkDeleteModalClose,
  isBulkDeleting = false,
}) => {
  const { t } = useTranslation('admin');
  const [isCategoriesModalOpen, setIsCategoriesModalOpen] = useState(false);

  const handleSelectionChange = (glossaryId: string, selected: boolean) => {
    if (!onSelectionChange) return;
    if (selected) {
      onSelectionChange([...selectedGlossaryIds, glossaryId]);
    } else {
      onSelectionChange(selectedGlossaryIds.filter((id) => id !== glossaryId));
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('glossaries.title')}</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('glossaries.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          {selectedGlossaryIds.length > 0 && onBulkDelete && (
            <Button color="danger" variant="flat" onPress={onBulkDelete} startContent={<Trash2 className="w-4 h-4" />}>
              {t('glossaries.actions.bulkDelete', { count: selectedGlossaryIds.length })}
            </Button>
          )}
          <Button
            variant="flat"
            startContent={<FolderOpen className="w-4 h-4" />}
            onPress={() => setIsCategoriesModalOpen(true)}
          >
            {t('glossaries.categories.manageButton')}
          </Button>
          <Button color="primary" onPress={onCreate}>
            {t('glossaries.createButton')}
          </Button>
        </div>
      </div>

      {/* Controls */}
      <div className="flex justify-between gap-3 items-end flex-wrap">
        <Input
          isClearable
          className="w-full sm:max-w-[44%]"
          placeholder={t('glossaries.search.placeholder')}
          startContent={<SearchIcon />}
          value={searchQuery}
          onClear={() => onSearchChange('')}
          onValueChange={onSearchChange}
        />
        <div className="flex gap-3 flex-wrap">
          <div className="w-[150px]">
            <CategorySelector
              value={selectedCategoryId || 'all'}
              onChange={(value) => onCategoryChange(value === 'all' ? undefined : value)}
              placeholder={t('glossaries.table.columns.category')}
              variant="flat"
              label="Categories"
              showAllOption={true}
            />
          </div>
          {hasActiveFilters && (
            <Button variant="flat" onPress={onClearFilters} startContent={<X className="w-4 h-4" />}>
              {t('glossaries.filters.clear')}
            </Button>
          )}
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Loading State */}
      {isLoading && glossaries.length === 0 && (
        <div className="flex justify-center items-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && glossaries.length === 0 && !error && (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {t('glossaries.table.empty.title')}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{t('glossaries.table.empty.message')}</p>
          <Button color="primary" onPress={onCreate}>
            {t('glossaries.table.empty.action')}
          </Button>
        </div>
      )}

      {/* Glossary Cards */}
      {glossaries.length > 0 && (
        <div className="space-y-4">
          {glossaries.map((glossary) => (
            <GlossaryCard
              key={glossary.id}
              glossary={glossary}
              onEdit={onEdit}
              onDelete={onDelete}
              isSelected={selectedGlossaryIds.includes(glossary.id)}
              onSelectionChange={
                onSelectionChange ? (selected) => handleSelectionChange(glossary.id, selected) : undefined
              }
            />
          ))}
        </div>
      )}

      {/* Infinite Scroll Trigger */}
      {hasNextPage && (
        <div ref={loadMoreRef as React.RefObject<HTMLDivElement>} className="flex justify-center py-4">
          {isFetchingNextPage && <Spinner size="lg" />}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={onDeleteModalClose}
        onConfirm={onDeleteConfirm}
        title={t('glossaries.modals.delete.title')}
        message={t('glossaries.modals.delete.message')}
        confirmLabel={t('glossaries.modals.delete.confirm')}
        cancelLabel={t('glossaries.modals.delete.cancel')}
        confirmColor="danger"
        isLoading={isDeleting}
      />

      {/* Bulk Delete Confirmation Modal */}
      {onBulkDeleteConfirm && onBulkDeleteModalClose && (
        <ConfirmationModal
          isOpen={bulkDeleteModalOpen}
          onClose={onBulkDeleteModalClose}
          onConfirm={onBulkDeleteConfirm}
          title={t('glossaries.modals.bulkDelete.title')}
          message={t('glossaries.modals.bulkDelete.message', { count: selectedGlossaryIds.length })}
          confirmLabel={t('glossaries.modals.bulkDelete.confirm')}
          cancelLabel={t('glossaries.modals.bulkDelete.cancel')}
          confirmColor="danger"
          isLoading={isBulkDeleting}
        />
      )}

      {/* Categories Management Modal */}
      <CategoriesModal isOpen={isCategoriesModalOpen} onClose={() => setIsCategoriesModalOpen(false)} />
    </div>
  );
};

export default GlossariesTab;
